import { FC, useCallback, useEffect, useRef } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { RightBlackModal } from '../../right-modal';
import { PlusIcon } from '../../../icons';
import { CoreLinkButton } from '../../';
import { EditEventSponsorDto, waitAsync } from '@sweatpals/shared';
import { SponsorCard } from './sponsor-card.component';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { EventSponsorsFormValidator } from '../../../models/dto';
import { customResetForm, scrollToFirstErrorElement } from '../../../utils';

const resolver = classValidatorResolver(EventSponsorsFormValidator);

const defaultValues: EditEventSponsorDto[] = [
	{
		sponsorName: '',
		sponsorUrl: '',
		avatarImg: null,
	},
];

export interface SponsorsModalProps<TValues = EditEventSponsorDto> {
	open: boolean;
	data?: TValues[];
	onClose?(isDirty?: boolean): void;
	getPublicFileUrl(fileId: string): string;
	onSubmit?(data: EventSponsorsFormValidator): void;
}

export const SponsorsModal: FC<SponsorsModalProps> = props => {
	const {
		data = defaultValues,
		onSubmit,
		open,
		onClose,
		getPublicFileUrl,
	} = props;

	const formRef = useRef<HTMLDivElement>(null);

	const form = useForm<EventSponsorsFormValidator>({
		defaultValues: structuredClone({ sponsors: data }),
		resolver,
		mode: 'onBlur',
	});

	const {
		fields: sponsors,
		append,
		remove,
		move,
	} = useFieldArray({
		control: form.control,
		name: 'sponsors',
		keyName: 'key',
	});

	const handleAddSponsor = () => {
		append({
			sponsorName: '',
			sponsorUrl: '',
			avatarImg: null,
		});
	};

	const handleRemoveSponsor = (index: number) => {
		remove(index);
	};

	useEffect(() => {
		if (open) {
			customResetForm(form, structuredClone({ sponsors: data }));
		}
	}, [open, data, form]);

	const handleSave = async (values: EventSponsorsFormValidator) => {
		await waitAsync(500);

		onSubmit?.(values);
	};

	const handleClose = useCallback(() => {
		onClose?.(form.formState.isDirty);
	}, [onClose, form]);

	const validateForm = useCallback(async (): Promise<void> => {
		await form.trigger();

		scrollToFirstErrorElement(formRef.current);
	}, [form]);

	return (
		<FormProvider {...form}>
			<RightBlackModal
				open={open}
				onClose={handleClose}
				headerTitle='Add Sponsors'
				modalProps={{
					component: 'form',
					onSubmit: form.handleSubmit(handleSave, validateForm),
				}}
				submitButtonProps={{
					type: 'submit',
					loading: form.formState.isSubmitting,
				}}
				contentBoxRef={formRef}
			>
				{sponsors?.map((sponsor, index) => (
					<SponsorCard
						key={sponsor.key}
						id={sponsor.key}
						canDrag={sponsors.length > 1}
						controlId={`sponsors.${index}`}
						getPublicFileUrl={getPublicFileUrl}
						onRemove={handleRemoveSponsor}
						index={index}
						onMove={move}
					/>
				))}

				<CoreLinkButton type="button" onClick={handleAddSponsor}>
					<PlusIcon width={16} height={16} />
					Add sponsor
				</CoreLinkButton>
			</RightBlackModal>
		</FormProvider>
	);
};
